import { useDispatch } from 'react-redux';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  deleteSourceV2,
  setFilterType,
  setRightOperand,
  setShouldExclude,
  updateFilterOperator,
} from '../../../../redux/Calculations/CalculationsActions';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux';
import { Box, Button, Divider, Menu, MenuItem, Radio, Tooltip } from '@mui/material';
import { fontSize, palette } from '../../../..';
import { ChevronRight } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { TFilterType } from '../../../../types/types';
import { useEffect, useState } from 'react';
import React from 'react';
import { checkIfMissingColumn } from '../../../CommonFunctions/CommonFunctions';

interface LeftOperandHeaderProps {
  leftOperandName: string;
  operandIndex: number;
  conditionFilterId: string;
  activeFlow: string;
  propKey: string;
  filterType: TFilterType;
  shouldExclude: boolean;
  onExpandCollapse: (value: boolean) => void;
  dataType: string;
  showWarning?: boolean;
  disabled?: boolean;
  borderColor?: string;
}
const LeftOperandHeader = React.memo(
  ({
    operandIndex,
    conditionFilterId,
    activeFlow,
    propKey,
    leftOperandName,
    dataType,
    disabled,
    shouldExclude,
    filterType,
    showWarning = false,
    borderColor = '#af99db',
    onExpandCollapse,
  }: LeftOperandHeaderProps) => {
    const { currentCalculation, chartProperties, tabTileProps } = useSelector(
      (state: RootState) => {
        return {
          currentCalculation: state.calculations.properties[propKey].currentCalculationSession,
          chartProperties: state.chartProperties,
          tabTileProps: state.tabTileProps,
        };
      },
    );
    const flows = currentCalculation?.calculationInfo.flows;
    const fields = currentCalculation?.calculationInfo.fields;
    const dispatch = useDispatch();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isShowWarning, setIsShowWarning] = useState<boolean>(false);

    const filterTypes: TFilterType[] = ['Pick List'];
    if (dataType !== 'boolean') {
      filterTypes.push('Search Condition');
    }
    if (dataType === 'date' || dataType === 'timestamp') {
      filterTypes.push('Relative Filter');
    }
    const selectedDs = chartProperties.properties[propKey].selectedDs;

    const handleExpandCollapse = () => {
      setIsCollapsed((prev) => !prev);
      onExpandCollapse(!isCollapsed);
    };
    const deleteSource = () => {
      if (disabled) return;
      dispatch(deleteSourceV2(activeFlow, '', propKey, 0, operandIndex));
    };
    const openFilterMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      setAnchorEl((prev) => (prev === null ? e.currentTarget : null));
    };

    useEffect(() => {
      if (!fields || !selectedDs) return;
      (async () => {
        const fieldsDetails = Object.values(fields);
        const field = fieldsDetails.find(
          (field) => field.fieldName === leftOperandName && field.dataType === dataType,
        );
        if (!field) return;
        const res = await checkIfMissingColumn(
          field,
          selectedDs,
          tabTileProps.tablesForSelectedDataSets[selectedDs.id],
        );
        setIsShowWarning(res.isMissing);
      })();
    }, [leftOperandName, dataType]);

    if (!flows) return null;
    return (
      <Box
        sx={{
          height: '1.5rem',
          minHeight: '1.5rem',
          padding: '0.3rem',
          border: isShowWarning ? '1px solid #850000ff' : `1px solid  ${borderColor}`,
          display: 'flex',
          alignItems: 'center',
          borderRadius: '5px',
          justifyContent: 'space-between',
          marginBottom: '0.4rem',
        }}
      >
        {disabled ? (
          <Tooltip
            title="This calculation is being used in another flow. Please remove the dependency before deleting."
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <LockOutlinedIcon
              style={{
                color: '#2BB9BB',
              }}
            />
          </Tooltip>
        ) : (
          <CloseRoundedIcon
            onClick={deleteSource}
            sx={{
              fontSize: fontSize.medium,
              cursor: disabled ? 'not-allowed' : 'pointer',
              color: palette.primary.contrastText,
              marginRight: '0.5rem',
              '&:hover': {
                color: disabled ? palette.primary.contrastText : 'red',
                transform: 'scale(1.1)',
              },
            }}
          />
        )}

        <span
          style={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <Tooltip
            title={leftOperandName}
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {leftOperandName}
            </div>
          </Tooltip>
          {showWarning && (
            <Tooltip
              title="Field is not filled properly"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <WarningAmberIcon
                style={{
                  color: 'orange',
                  marginLeft: '5px',
                  opacity: '0.7',
                  flexShrink: 0,
                }}
                fontSize="small"
              />
            </Tooltip>
          )}
        </span>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, mr: "3px", gap : "2px" }}>
          <Button
            type="button"
            className="buttonCommon"
            style={{ backgroundColor: 'transparent' }}
            title="More Options"
            disabled={disabled}
            onClick={openFilterMenu}
            sx={{
              minWidth: 'fit-content',
              padding: 0,
            }}
          >
            <MoreVertIcon
              style={{
                fontSize: fontSize.extraLarge,
                color: palette.primary.contrastText,
              }}
            />
          </Button>
          <Button
            onClick={handleExpandCollapse}
            sx={{
              minWidth: 'fit-content',
              padding: 0,
            }}
          >
            {isCollapsed ? (
              <ChevronRight
                sx={{
                  color: palette.primary.contrastText,
                  padding: 0,
                }}
              />
            ) : (
              <KeyboardArrowDownIcon
                sx={{
                  color: palette.primary.contrastText,
                  padding: 0,
                }}
              />
            )}
          </Button>
        </Box>

        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={() => setAnchorEl(null)}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {filterTypes.map((_filterType, index) => {
            return (
              <div
                key={index}
                style={{ display: 'flex' }}
                onClick={() => {
                  if (disabled) return;
                  if (_filterType === filterType) return;
                  dispatch(setFilterType(propKey, _filterType, operandIndex, conditionFilterId));
                  dispatch(setRightOperand(propKey, conditionFilterId, operandIndex, [], []));
                  dispatch(
                    updateFilterOperator(
                      propKey,
                      conditionFilterId,
                      operandIndex,
                      _filterType === 'Pick List'
                        ? 'in'
                        : _filterType === 'Relative Filter'
                          ? 'relativeFilter'
                          : dataType === 'text'
                            ? 'beginsWith'
                            : 'greaterThan',
                    ),
                  );
                  setAnchorEl(null);
                }}
              >
                <Tooltip
                  title={filterType === _filterType ? 'Selected' : null}
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <Radio
                    disabled={disabled}
                    checked={filterType === _filterType}
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fontSize: '12px',
                        height: '12px',
                        color: '#af99db',
                      },
                      // fontSize: "0px",
                      alignSelf: 'center',
                      marginLeft: '5px',
                    }}
                  />
                </Tooltip>
                <MenuItem
                  key={index}
                  sx={{
                    flex: 1,
                    fontSize: '12px',
                    alignSelf: 'center',
                    padding: '2px 0px',
                    paddingRight: '1rem',
                  }}
                >
                  {_filterType}
                </MenuItem>
              </div>
            );
          })}
          <Divider
            sx={{
              margin: '5px 0px',
            }}
          />

          {/* Include */}
          <div
            style={{ display: 'flex' }}
            onClick={() => {
              if (disabled) return;
              dispatch(setShouldExclude(propKey, conditionFilterId, operandIndex, false));
              setAnchorEl(null);
            }}
          >
            <Tooltip
              title={!shouldExclude ? 'Selected' : null}
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <Radio
                checked={!shouldExclude}
                disabled={disabled}
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: '12px',
                    height: '12px',
                    color: '#af99db',
                  },
                  alignSelf: 'center',
                  marginLeft: '5px',
                }}
              />
            </Tooltip>
            <MenuItem
              sx={{
                flex: 1,
                fontSize: '12px',
                alignSelf: 'center',
                padding: '2px 0px',
                paddingRight: '1rem',
              }}
            >
              Include
            </MenuItem>
          </div>

          {/* Exclude */}
          <div
            style={{ display: 'flex' }}
            onClick={() => {
              if (disabled) return;
              dispatch(setShouldExclude(propKey, conditionFilterId, operandIndex, true));
              setAnchorEl(null);
            }}
          >
            <Tooltip
              title={shouldExclude ? 'Selected' : null}
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <Radio
                disabled={disabled}
                checked={shouldExclude}
                sx={
                  shouldExclude
                    ? {
                        '& .MuiSvgIcon-root': {
                          fontSize: '12px',
                          height: '12px',
                          color: '#ffb74d',
                        },
                        alignSelf: 'center',
                        marginLeft: '5px',
                      }
                    : {
                        '& .MuiSvgIcon-root': {
                          fontSize: '12px',
                          height: '12px',
                          color: '#af99db',
                        },
                        alignSelf: 'center',
                        marginLeft: '5px',
                      }
                }
              />
            </Tooltip>
            <MenuItem
              sx={{
                flex: 1,
                fontSize: '12px',
                alignSelf: 'center',
                padding: '2px 0px',
                paddingRight: '1rem',
              }}
            >
              Exclude
            </MenuItem>
          </div>
        </Menu>
      </Box>
    );
  },
);

export default LeftOperandHeader;
